import { API_BASE } from "../config";
import type { Language, LeadKind, PriceOffer } from "../types";

/** The previews this browser has opened, so a page can show its reader the
 *  others they asked for (the strip at the top, Others.tsx).
 *
 *  Only the links, newest first, and only on this device: the server answers
 *  about a link this browser sends it and nothing else, so a page forwarded to
 *  somebody never shows them the rest. Nothing here is sent anywhere but
 *  /api/others, and never with tracking. */

const KEY = "minime.previews";

/** As many as the server will look at in one answer. */
const KEEP = 12;

/** One of their previews as the strip shows it (otherPreviews on the server). */
export interface OtherPreview {
  token: string;
  /** The page this was asked from. */
  current: boolean;
  state: "ready" | "pending";
  ordered: boolean;
  kind: LeadKind;
  pieces: number;
  thumb: string | null;
  receivedAt: string | null;
  /** What "Order them together" on this page can take of it: its pieces with
   *  their prices, in this page's language and currency at its own code.
   *  Absent unless paying for several previews at once is offered here. */
  basket?: BasketPiece[] | null;
  /** Why it cannot be added here when it could have been: another currency,
   *  not open to a payment now, or one more than one payment takes. */
  notHere?: "currency" | "closed" | "limit" | null;
}

/** A piece of another of their previews, as "Order them together" offers it. */
export interface BasketPiece {
  id: string;
  kind: LeadKind;
  pets: number;
  drawingUrl: string | null;
  photoUrl: string | null;
  price: PriceOffer;
}

function stored(): string[] {
  try {
    const value = JSON.parse(localStorage.getItem(KEY) ?? "[]");
    return Array.isArray(value) ? value.filter((t): t is string => typeof t === "string") : [];
  } catch {
    return []; // private browsing, or something else wrote the key
  }
}

function store(tokens: string[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(tokens.slice(0, KEEP)));
  } catch {
    /* private browsing: this visit simply has nothing to show */
  }
}

/** This page, to the front of the list. */
export function rememberPreview(token: string): void {
  if (!token) return;
  const now = stored();
  if (now[0] === token) return;
  store([token, ...now.filter((t) => t !== token)]);
}

/** Pages that are gone, or open nothing any more. */
export function forgetPreviews(tokens: string[]): void {
  if (!tokens.length) return;
  const now = stored();
  const next = now.filter((t) => !tokens.includes(t));
  if (next.length !== now.length) store(next);
}

/** Every page this browser has opened but this one. */
export function knownOthers(token: string): string[] {
  return stored().filter((t) => t !== token);
}

/** Which of them are the same person's, and where each has got to. Null when
 *  it could not be asked; the strip is a convenience, and its absence is the
 *  page as it always was. */
export async function fetchOthers(
  token: string,
  known: string[],
  lang?: Language | null
): Promise<OtherPreview[] | null> {
  try {
    const r = await fetch(`${API_BASE}/api/others`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // The language the page is read in, which the others' prices in "Order
      // them together" are quoted in, as this page's own are.
      body: JSON.stringify({ token, known, ...(lang ? { lang } : {}) }),
    });
    if (!r.ok) return null;
    const data = (await r.json()) as { previews?: OtherPreview[]; gone?: string[] };
    forgetPreviews(Array.isArray(data.gone) ? data.gone : []);
    return Array.isArray(data.previews) ? data.previews : [];
  } catch {
    return null;
  }
}
